import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Dropdown, FormControl, Row } from 'react-bootstrap';
import { EllipsisVertical, Mail, Pencil, Phone, Plus, RefreshCw, User, Users } from 'lucide-react';

const getStatusLabel = isEnabled => (isEnabled ? 'Enabled' : 'Disabled');

const UserCard = ({ user, onEdit, onStatusChange, onReinvite, onClick }) => {
  const { name, phone, email, role, isEnabled } = user;
  const statusLabel = getStatusLabel(!!isEnabled);
  const [isHovering, setIsHovering] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const isLoggedInUser = user._id === JSON.parse(localStorage.getItem('user'))?._id;

  const getRoleVariant = role => {
    const map = {
      'Org Admin': '#ebc221',
      'Org Staff': '#0069fc'
    };
    return map[role];
  };

  const getStatusVariant = status => {
    if (status === 'Enabled') return 'success';
    if (status === 'Disabled') return 'danger';
    return 'secondary';
  };

  const handleStatusSelect = newStatus => {
    if (!onStatusChange) return;
    onStatusChange(newStatus);
  };

  return (
    <Card
      className="position-relative"
      style={{
        padding: '20px 20px 20px 24px',
        transition: 'all 0.05s ease',
        backgroundColor: isHovering ? '#f3f4f7' : 'transparent',
        overflow: 'hidden'
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Border element */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: isHovering ? '6px' : '4px',
          backgroundColor: getRoleVariant(role) || 'grey',
          transition: 'all 0.2s ease'
        }}
      />
      <div className="pe-4">
        {(onEdit || onReinvite) && !isLoggedInUser && (
          <div style={{ position: 'absolute', top: 8, right: 8 }}>
            <Dropdown drop="down-start" show={showDropdown} onToggle={setShowDropdown}>
              <Dropdown.Toggle
                as={Button}
                size="sm"
                variant="outline-light"
                className="p-0 d-flex align-items-center justify-content-center border-0 text-dark dropdown-toggle-no-arrow"
                style={{ width: 28, height: 28 }}
              >
                <EllipsisVertical size={14} />
              </Dropdown.Toggle>
              <Dropdown.Menu align="start" style={{ minWidth: '100px' }}>
                {onEdit && (
                  <Dropdown.Item
                    onClick={e => {
                      e.stopPropagation();
                      onEdit();
                    }}
                    className="d-flex align-items-center"
                  >
                    <Pencil size={14} className="mr-2" />
                    Edit
                  </Dropdown.Item>
                )}
                {onReinvite && (
                  <Dropdown.Item
                    onClick={e => {
                      e.stopPropagation();
                      onReinvite();
                    }}
                    className="d-flex align-items-center"
                  >
                    <RefreshCw size={14} className="mr-2" />
                    Reinvite
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}

        <div className="d-flex align-items-center mb-3">
          <div
            className="d-flex align-items-center justify-content-center mr-2 text-info "
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              color: 'black',
              // fontSize: 2,
              fontWeight: 'bold',
              backgroundColor: isHovering ? '#dfe6f5' : '#e6f5ff',
              border: isHovering ? '1px solid #237fff' : '1px solid #306ee833',
              transition: 'all 0.03s ease'
            }}
          >
            {name ? (
              <>
                {name
                  ?.split(' ')[0]
                  .slice(0, 1)
                  .toUpperCase()}
                {name
                  ?.split(' ')[1]
                  ?.slice(0, 1)
                  .toUpperCase()}
              </>
            ) : (
              <User size={24} />
            )}
          </div>
          <div>
            {/* Name */}
            <Card.Title className="midFont font-weight-semibold mb-2">{name || '-'}</Card.Title>

            {/* Role and status */}
            <div className="">
              <div className="d-flex align-items-center" style={{ gap: 4 }}>
                <Badge pill style={{ backgroundColor: getRoleVariant(role) }} className="py-1">
                  <span className="xxsmallFont text-white">{role || '-'}</span>
                </Badge>

                <div className="d-flex align-items-center">
                  {!isLoggedInUser && onStatusChange ? (
                    <Dropdown alignRight className="d-flex align-items-center">
                      <Dropdown.Toggle
                        as={Badge}
                        pill
                        variant={getStatusVariant(statusLabel)}
                        className="py-1"
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="xxsmallFont mx-1">{statusLabel}</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu align="left">
                        <Dropdown.Item
                          onClick={e => {
                            e.stopPropagation();
                            handleStatusSelect('Enabled');
                          }}
                        >
                          Enabled
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={e => {
                            e.stopPropagation();
                            handleStatusSelect('Disabled');
                          }}
                        >
                          Disabled
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : (
                    <Badge pill variant={getStatusVariant(statusLabel)} className="py-1">
                      <span className="font-weight-bold">{statusLabel}</span>
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact info */}
        <div className="text-muted">
          <p className="mb-1">
            <Phone size={14} className="mr-1" /> {phone || '-'}
          </p>
          <p className="mb-0 text-break">
            <Mail size={14} className="mr-1" /> {email || '-'}
          </p>
        </div>
      </div>
    </Card>
  );
};

const UsersCardView = ({
  users = [],
  searchQueryKeys = [],
  onAddClick,
  onEditUser,
  onChangeUserStatus,
  onReinviteUser
}) => {
  const [query, setQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState(users);

  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setFilteredUsers(users);
      return;
    }

    setFilteredUsers(
      users.filter(user =>
        searchQueryKeys.some(key => {
          const value = user[key];
          if (value === undefined || value === null) return false;
          return String(value)
            .trim()
            .toLowerCase()
            .includes(q);
        })
      )
    );
  }, [users, query, searchQueryKeys]);

  return (
    <div className="d-flex flex-column" style={{ maxHeight: '70vh' }}>
      <div className="d-flex mb-2 w-100">
        <div className="flex-grow-1">
          <FormControl
            size="sm"
            placeholder="Quick search..."
            className="entity-table-input-field"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        {onAddClick && (
          <div className="ml-2">
            <Button
              size="sm"
              variant="info"
              onClick={onAddClick}
              className="d-flex align-items-center"
              style={{ minHeight: '40px' }}
            >
              <Plus size={14} className="mr-1" />
              <span>Invite User</span>
            </Button>
          </div>
        )}
      </div>

      <div className="flex-grow-1 px-0 mt-2" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
        {filteredUsers.length === 0 ? (
          <div className="d-flex flex-column justify-content-center align-items-center h-100">
            {users.length === 0 ? (
              <Card
                className="text-center w-100"
                style={{
                  padding: '48px 32px',
                  border: '1px dashed #dee2e6',
                  backgroundColor: '#ffffff'
                }}
              >
                <div className="mb-4 d-flex justify-content-center">
                  <div
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      backgroundColor: '#e3f2fd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#1976d2'
                    }}
                  >
                    <Users size={40} />
                  </div>
                </div>
                <h6 className="mb-3 text-muted" style={{ fontSize: '16px', fontWeight: 500 }}>
                  No users in this organization
                </h6>
                {onAddClick && (
                  <Button
                    variant="info"
                    onClick={onAddClick}
                    className="d-flex align-items-center mx-auto"
                    style={{ minHeight: '40px' }}
                  >
                    <Plus size={14} className="mr-2" />
                    <span>Invite User</span>
                  </Button>
                )}
              </Card>
            ) : (
              <h6 className="mb-0">Nothing to show</h6>
            )}
          </div>
        ) : (
          <Row style={{ marginLeft: -12, padding: '0 4px' }}>
            {filteredUsers.map(user => (
              <Col key={user._id} xs={12} md={6} lg={4} xl={3} style={{ padding: '12px' }}>
                <UserCard
                  user={user}
                  onEdit={onEditUser ? () => onEditUser(user) : undefined}
                  onStatusChange={onChangeUserStatus ? status => onChangeUserStatus(user, status) : undefined}
                  onReinvite={onReinviteUser ? () => onReinviteUser(user) : undefined}
                />
              </Col>
            ))}
          </Row>
        )}
      </div>
    </div>
  );
};

export default UsersCardView;
